import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Clipboard } from '@angular/cdk/clipboard';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgxCurrencyDirective } from 'ngx-currency';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { AtivacaoSectionCardComponent } from '@shared/components/ativacao-section-card/ativacao-section-card.component';
import { ActionCardComponent } from '../../../shared/components/action-card/action-card.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ResulthLocalConnectSaibaMaisDialogComponent } from './resulth-local-connect-saiba-mais-dialog/resulth-local-connect-saiba-mais-dialog.component';
import { requiredTrim } from '../../../shared/validators/string-trim.validator';

import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { AuthService } from '../../../shared/services/apis/usuario/auth.service';
import { EmpresaService } from '../../../shared/services/apis/usuario/empresa.service';
import { ResulthLocalConnectResponse } from '../../../shared/services/responses/usuario/resulth-local-connect.response';
import { EmpresaResulthLocalConnectRequest } from '../../../shared/services/requests/usuario/empresa-resulth-local-connect.request';
import { UsuarioLoginResponse } from '../../../shared/services/responses/usuario/usuario-login.response';
import { Funcionalidade } from '../../../shared/services/models/usuario/funcionalidade.model';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { Masks, MaskService } from '../../../shared/services/mask.service';

import { DatabaseService } from '../../../shared/services/apis/interface-resulth-local-connect/database.service';
import { DatabaseConnectionTestRequest } from '../../../shared/services/requests/interface-resulth-local-connect/database-connection-test.request';
import { AcessoResulthLocalConnectService } from '../../../shared/services/apis/interface-resulth-local-connect/acesso-resulth-local-connect.service';
import { AcessoResulthLocalConnectRequest } from '../../../shared/services/requests/interface-resulth-local-connect/acesso-resulth-local-connect.request';
import { AcessoResulthLocalConnectResponse } from '../../../shared/services/responses/interface-resulth-local-connect/acesso-resulth-local-connect.response';
import { AcessoResulthLocalConnectStruct } from '../../../shared/services/structs/interface-resulth-local-connect/acesso-resulth-local-connect.struct';
import { TokenAcessoResulthLocalConnectResponse } from '../../../shared/services/responses/interface-resulth-local-connect/token-acesso-resulth-local-connect.response';

@Component({
  selector: 'app-resulth-local-connect',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTooltipModule,
    NgxMaskDirective,
    NgxCurrencyDirective,
    HeaderComponent,
    FooterActionComponent,
    AtivacaoSectionCardComponent,
    ActionCardComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './resulth-local-connect.component.html',
  styleUrl: './resulth-local-connect.component.css',
})
export class ResulthLocalConnectComponent implements OnInit {
  public moduloEnum = ModuloEnum.Integracao;
  public funcionalidadeEnum = FuncionalidadeEnum.Integracao_ResulthLocalConnect;

  public model: FormGroup;
  public masks: Masks;
  public integerMask = this.maskService.getIntegerMask();

  public _isLoadingButtonSalvar: boolean = false;
  public _isLoadingButtonTestarConexao: boolean = false;
  public buttonDesativado: boolean = false;
  public habilitaTesteConexao: boolean = false;

  public _resulthLocalConnectResponse: ResulthLocalConnectResponse;
  public _acessoResulthLocalConnectResponse: AcessoResulthLocalConnectResponse | null;
  public userLoginResponse: UsuarioLoginResponse;

  public isEmpresaMatriz: boolean = false;
  public podeExibirToggleTokenMatriz: boolean = false;
  public empresasFiliaisUsandoToken: any[] = [];

  public bkpSenhaBDResulthLocal: string = '';
  public placeholderSenhaBDResulthLocal: string = '************';

  private _acessoFormSnapshot: {
    token?: string;
    host?: string;
    porta?: number;
    caminho?: string;
    usuario?: string;
    senha?: string;
    codFilial?: string;
  } | null = null;

  private _tokenGeradoAutomaticamente = false;
  private _tokenCacheLocal: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private empresaService: EmpresaService,
    private acessoResulthLocalConnectService: AcessoResulthLocalConnectService,
    private databaseService: DatabaseService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router,
    private maskService: MaskService,
    private authService: AuthService,
    private clipboard: Clipboard
  ) {}

  async ngOnInit(): Promise<void> {
    this.masks = this.maskService.getMasks();

    this.model = this.formBuilder.group({
      utilizaResulthLocalConnect: new FormControl(false),
      usaTokenMatrizResulthLocalConnect: new FormControl(false),
      tokenResulthLocalConnect: new FormControl({ value: '', disabled: true }, [
        Validators.required,
        requiredTrim,
      ]),
      hostBDResulthLocal: new FormControl('localhost', [Validators.required, requiredTrim]),
      portaBDResulthLocal: new FormControl(3050, [Validators.required, requiredTrim]),
      caminhoBDResulthLocal: new FormControl('', [Validators.required, requiredTrim]),
      usuarioBDResulthLocal: new FormControl('', [Validators.required, requiredTrim]),
      senhaBDResulthLocal: new FormControl({ value: '', disabled: true }, [
        Validators.required,
        requiredTrim,
      ]),
      codFilialBDResulthLocal: new FormControl('', Validators.required),
    });

    this.validarPermissoes();
    await this.popularDadosEmpresa();

    this.model.get('utilizaResulthLocalConnect')!.valueChanges.subscribe(async (on: boolean) => {
      if (!on && this.isEmpresaMatriz && this.empresasFiliaisUsandoToken.length > 0) {
        this.model.get('utilizaResulthLocalConnect')?.setValue(true, { emitEvent: false });
        this.modalConfirmDisableLocalConnectMatriz();
        return;
      }
      await this.processaTrocaUtilizaResulthLocalConnect(on);
    });

    this.model
      .get('usaTokenMatrizResulthLocalConnect')!
      .valueChanges.subscribe(async (usaTokenMatriz: boolean) => {
        if (!this.model.get('utilizaResulthLocalConnect')?.value) {
          return;
        }
        if (usaTokenMatriz) {
          const tokenAtual = this.model.get('tokenResulthLocalConnect')?.value;
          if (tokenAtual) this._tokenCacheLocal = tokenAtual;
          await this.carregarTokenDaMatriz();
        } else {
          this.model.get('tokenResulthLocalConnect')?.enable({ emitEvent: false });
          if (this._tokenCacheLocal) {
            this.model.get('tokenResulthLocalConnect')?.setValue(this._tokenCacheLocal);
          } else {
            this.gerarPrimeiroToken();
          }
        }
        this.configResulthLocalConnectChange();
      });
  }

  validarPermissoes(): void {
    this.userLoginResponse = this.authService.getUserInfos();
    this.isEmpresaMatriz = !!this.userLoginResponse.empresaPrincipalGrupo;

    if (!this.userLoginResponse) {
      this.router.navigate(['/']);
      return;
    }

    const listaFuncinalidade: Funcionalidade[] =
      this.userLoginResponse.listaFuncionalidades.filter(funcionalidade => {
        return funcionalidade.idFuncionalidade == FuncionalidadeEnum.Integracao_ResulthLocalConnect;
      });

    if (listaFuncinalidade.length == 0) {
      this.router.navigate(['/integracao']);
      return;
    }
  }

  async popularDadosEmpresa(): Promise<void> {
    try {
      const resulthLocalConnectResponse = await firstValueFrom(
        this.empresaService.obterResulthLocalConnectPorId(this.userLoginResponse.idEmpresa)
      );

      if (resulthLocalConnectResponse.isError) {
        this.alertService.show('Erro', resulthLocalConnectResponse.errorDescription, AlertType.error);
        return;
      }

      this._resulthLocalConnectResponse = resulthLocalConnectResponse;
      this.model
        .get('utilizaResulthLocalConnect')
        ?.setValue(resulthLocalConnectResponse.utilizaResulthLocalConnect);

      const temGrupo = !!resulthLocalConnectResponse.idEmpresaMatriz;
      const matrizUsaResulth = !!resulthLocalConnectResponse.matrizUsaResulthLocalConnect;
      this.podeExibirToggleTokenMatriz = !this.isEmpresaMatriz && temGrupo && matrizUsaResulth;

      if (resulthLocalConnectResponse.utilizaResulthLocalConnect) {
        if (!(await this.buscarAcessoResulthLocalConnect())) return;

        const usaTokenMatriz =
          !!this._acessoResulthLocalConnectResponse?.acessoResulthLocalConnect?.utilizaTokenMatriz;
        this.model
          .get('usaTokenMatrizResulthLocalConnect')
          ?.setValue(usaTokenMatriz, { emitEvent: false });

        this.model
          .get('tokenResulthLocalConnect')
          ?.setValue(this._acessoResulthLocalConnectResponse?.acessoResulthLocalConnect?.token);

        if (this.model.get('usaTokenMatrizResulthLocalConnect')?.value) {
          this.model.get('tokenResulthLocalConnect')?.disable({ emitEvent: false });
        } else {
          this.model.get('tokenResulthLocalConnect')?.enable({ emitEvent: false });
        }
        this.model
          .get('hostBDResulthLocal')
          ?.setValue(
            !this._acessoResulthLocalConnectResponse?.acessoResulthLocalConnect?.hostBDResulthLocal
              ? 'localhost'
              : this._acessoResulthLocalConnectResponse?.acessoResulthLocalConnect?.hostBDResulthLocal
          );
        this.model
          .get('portaBDResulthLocal')
          ?.setValue(
            !this._acessoResulthLocalConnectResponse?.acessoResulthLocalConnect?.portaBDResulthLocal
              ? 3050
              : this._acessoResulthLocalConnectResponse?.acessoResulthLocalConnect?.portaBDResulthLocal
          );
        this.model
          .get('caminhoBDResulthLocal')
          ?.setValue(
            this._acessoResulthLocalConnectResponse?.acessoResulthLocalConnect?.caminhoBDResulthLocal
          );
        this.model
          .get('usuarioBDResulthLocal')
          ?.setValue(
            this._acessoResulthLocalConnectResponse?.acessoResulthLocalConnect?.usuarioBDResulthLocal
          );
        this.bkpSenhaBDResulthLocal =
          this._acessoResulthLocalConnectResponse?.acessoResulthLocalConnect?.senhaBDResulthLocal!;
        this.model.get('senhaBDResulthLocal')?.setValue(this.placeholderSenhaBDResulthLocal);
        this.model
          .get('codFilialBDResulthLocal')
          ?.setValue(
            this._acessoResulthLocalConnectResponse?.acessoResulthLocalConnect?.codFilialBDResulthLocal
          );
      } else {
        this.model.get('tokenResulthLocalConnect')?.setValue('');
        this.model.get('hostBDResulthLocal')?.setValue('');
        this.model.get('portaBDResulthLocal')?.setValue('');
        this.model.get('caminhoBDResulthLocal')?.setValue('');
        this.model.get('usuarioBDResulthLocal')?.setValue('');
        this.model.get('senhaBDResulthLocal')?.setValue('');
        this.model.get('codFilialBDResulthLocal')?.setValue('');
      }

      this.configResulthLocalConnectChange();
    } catch (error) {
      this.alertService.show('Erro inesperado', error as any, AlertType.error);
    }
  }

  private async buscarAcessoResulthLocalConnect(): Promise<boolean> {
    this._isLoadingButtonSalvar = true;
    this.buttonDesativado = true;

    try {
      const resp = await firstValueFrom(
        this.acessoResulthLocalConnectService.get(this.userLoginResponse.idEmpresa)
      );

      this._isLoadingButtonSalvar = false;
      this.buttonDesativado = false;

      if (resp.isError) {
        this.alertService.show('Erro', resp.errorDescription, AlertType.error);
        return false;
      }

      this._acessoResulthLocalConnectResponse = resp;

      if (this.isEmpresaMatriz) {
        const idsUsandoToken = resp.acessoResulthLocalConnect?.idEmpresasUsandoTokenMatriz ?? [];
        const listaEmpresasDoGrupo = this.userLoginResponse?.listaEmpresasDoGrupo || [];

        this.empresasFiliaisUsandoToken = idsUsandoToken
          .map(id => listaEmpresasDoGrupo.find((emp: any) => emp.idEmpresa === id))
          .filter(emp => emp != null && emp.idEmpresa !== this.userLoginResponse.idEmpresa)
          .map((emp: any) => ({
            idEmpresa: emp.idEmpresa,
            nomeEmpresa: emp.nomeEmpresa,
            cnpj: emp.cnpj,
          }));
      }

      return true;
    } catch (error) {
      this._isLoadingButtonSalvar = false;
      this.alertService.show('Erro inesperado', error as any, AlertType.error);
      return false;
    }
  }

  public getTextoEmpresasUsandoToken(): string {
    if (!this.empresasFiliaisUsandoToken || this.empresasFiliaisUsandoToken.length === 0) {
      return 'Nenhuma filial vinculada';
    }

    const lista = this.empresasFiliaisUsandoToken
      .map(emp => {
        const cnpjLimpo = emp.cnpj?.toString().replace(/\D/g, '') || '';
        const format = (c: string) => c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
        const masked = cnpjLimpo.length === 14 ? format(cnpjLimpo) : cnpjLimpo;
        return `• ${emp.nomeEmpresa} (${masked})`;
      })
      .join('\n');

    return `Filiais vinculadas ao seu Token:\n${lista}`;
  }

  private modalConfirmDisableLocalConnectMatriz(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Atenção: Desativar Local Connect da Matriz',
        innerHtml: `
          <div style="line-height: 1.6; font-weight: 400;">
            <p style="margin-bottom: 1rem;">Você está prestes a desativar o <span style="font-weight: 600;">Local Connect</span> desta empresa Matriz.</p>
            <div style="background: rgba(255, 161, 23, 0.1); border-left: 4px solid #ffa117; padding: 12px; border-radius: 4px; color: #ffca28; margin-bottom: 1.5rem;">
              <span style="font-weight: 600;">Impacto importante:</span> Existem <span style="font-weight: 600;">${this.empresasFiliaisUsandoToken.length} filiais</span> vinculadas a esta matriz.
            </div>
            <p>Ao desativar:</p>
            <ul style="padding-left: 20px; margin-bottom: 1.5rem;">
              <li>A integração será <span style="font-weight: 600;">interrompida imediatamente</span> nas filiais.</li>
              <li>TODOS os tokens das filiais serão trocados.</li>
              <li>Será necessário reconfigurar o Local Connect em cada filial no futuro.</li>
            </ul>
            <p>Deseja realmente prosseguir?</p>
          </div>
        `,
        buttonConfirm: 'Sim, desativar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result && result.confirm) {
          this.model.get('utilizaResulthLocalConnect')?.setValue(false, { emitEvent: false });
          this.processaTrocaUtilizaResulthLocalConnect(false);
          this.alertService.show(
            'Aviso',
            'Lembre-se de salvar as alterações para aplicar a desativação.',
            AlertType.warning
          );
        }
      },
    });
  }

  private async processaTrocaUtilizaResulthLocalConnect(on: boolean): Promise<void> {
    const campos = [
      'tokenResulthLocalConnect',
      'hostBDResulthLocal',
      'portaBDResulthLocal',
      'caminhoBDResulthLocal',
      'usuarioBDResulthLocal',
      'senhaBDResulthLocal',
      'codFilialBDResulthLocal',
    ];

    if (!on) {
      this._acessoFormSnapshot = {
        token: this.model.get('tokenResulthLocalConnect')?.value ?? '',
        host: this.model.get('hostBDResulthLocal')?.value ?? '',
        porta: this.model.get('portaBDResulthLocal')?.value ?? (null as any),
        caminho: this.model.get('caminhoBDResulthLocal')?.value ?? '',
        usuario: this.model.get('usuarioBDResulthLocal')?.value ?? '',
        senha: this.model.get('senhaBDResulthLocal')?.value ?? '',
        codFilial: this.model.get('codFilialBDResulthLocal')?.value ?? '',
      };
    }

    campos.forEach(name => {
      const ctrl = this.model.get(name)!;
      if (on) {
        ctrl.setValidators([Validators.required, requiredTrim]);
      } else {
        ctrl.clearValidators();
      }
      ctrl.updateValueAndValidity({ emitEvent: false });
    });

    if (on) {
      if (this._acessoFormSnapshot) {
        this.model.get('tokenResulthLocalConnect')?.setValue(this._acessoFormSnapshot.token ?? '');
        this.model.get('hostBDResulthLocal')?.setValue(this._acessoFormSnapshot.host ?? 'localhost');
        this.model.get('portaBDResulthLocal')?.setValue(this._acessoFormSnapshot.porta ?? 3050);
        this.model.get('caminhoBDResulthLocal')?.setValue(this._acessoFormSnapshot.caminho ?? '');
        this.model.get('usuarioBDResulthLocal')?.setValue(this._acessoFormSnapshot.usuario ?? '');
        this.model.get('senhaBDResulthLocal')?.setValue(this.placeholderSenhaBDResulthLocal);
        this.model.get('codFilialBDResulthLocal')?.setValue(this._acessoFormSnapshot.codFilial ?? '');
      }

      const tokenAtual = (this.model.get('tokenResulthLocalConnect')?.value ?? '').toString().trim();
      if (!tokenAtual && !this._tokenGeradoAutomaticamente) {
        await this.gerarPrimeiroToken();
        this._tokenGeradoAutomaticamente = true;
      }
    }

    if (!on) {
      this.model.get('usaTokenMatrizResulthLocalConnect')?.setValue(false, { emitEvent: false });
      this.model.get('tokenResulthLocalConnect')?.enable({ emitEvent: false });
    }

    this.configResulthLocalConnectChange();
  }

  modalConfirmEditSenhaBDResulthLocal(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Alterar a senha',
        innerHtml: `<p>Deseja realizar a troca da senha para a conexão com o banco?</p>`,
        buttonConfirm: 'Alterar',
        buttonClose: 'Fechar',
      },
    });

    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result && result.confirm) this.editarSenha();
      },
    });
  }

  public editarSenha(): void {
    const senhaBancoControl = this.model.get('senhaBDResulthLocal');
    if (senhaBancoControl?.disabled) {
      senhaBancoControl.enable();
      senhaBancoControl.setValue('');
    }
  }

  modalConfirmChangeTokenResulthLocalConnect(): void {
    let innerHtml = '';

    if (this.isEmpresaMatriz && this.empresasFiliaisUsandoToken.length > 0) {
      innerHtml = `
        <div style="line-height: 1.6; font-weight: 400;">
          <p style="margin-bottom: 1rem;">Ao regerar o token, a comunicação com o seu sistema local será <span style="font-weight: 600;">interrompida imediatamente</span>.</p>
          <div style="background: rgba(255, 161, 23, 0.1); border-left: 4px solid #ffa117; padding: 12px; border-radius: 4px; color: #ffca28; margin-bottom: 1.5rem;">
            <span style="font-weight: 600;">Impacto nas filiais:</span> Existem <span style="font-weight: 600;">${this.empresasFiliaisUsandoToken.length} filiais</span> vinculadas ao seu token. Ao prosseguir, TODAS elas terão o token alterado para o novo valor automaticamente.
          </div>
          <p>Você precisará atualizar a configuração do Local Connect em sua infraestrutura para reativar a integração.</p>
          <p>Deseja continuar?</p>
        </div>
      `;
    } else {
      innerHtml = `
        <div style="line-height: 1.6; font-weight: 400;">
          <p>Ao regerar o token, a comunicação com o seu sistema local será <span style="font-weight: 600;">interrompida imediatamente</span>.</p>
          <p>Você precisará atualizar a configuração do ResulthLocalConnect com o novo token para reativar a integração.</p>
          <p>Deseja continuar?</p>
        </div>
      `;
    }

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Regerar o token',
        innerHtml: innerHtml,
        buttonConfirm: 'Sim, regerar',
        buttonClose: 'Não',
      },
    });

    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result && result.confirm) this.regerarToken();
      },
    });
  }

  public regerarToken(): void {
    this._isLoadingButtonSalvar = true;
    this.buttonDesativado = true;

    this.acessoResulthLocalConnectService.generateToken(this.userLoginResponse.idEmpresa).subscribe({
      next: (tokenAcessoResulthLocalConnectResponse: TokenAcessoResulthLocalConnectResponse) => {
        this._isLoadingButtonSalvar = false;
        this.buttonDesativado = false;

        if (tokenAcessoResulthLocalConnectResponse.isError) {
          this.alertService.show(
            'Erro',
            tokenAcessoResulthLocalConnectResponse.errorDescription,
            AlertType.error
          );
          return;
        }

        const token = tokenAcessoResulthLocalConnectResponse?.token?.trim() ?? '';
        if (token === '') {
          this.alertService.show(
            'Erro',
            'Foi gerado um token vazio, por favor, tente novamente.',
            AlertType.error
          );
          return;
        }

        this.model.get('tokenResulthLocalConnect')?.setValue(token);
        this.model.get('tokenResulthLocalConnect')?.markAsDirty();
        this.model.get('tokenResulthLocalConnect')?.markAsTouched();

        this.configResulthLocalConnectChange();
        this.alertService.show('Sucesso', 'Novo token gerado. Salve para aplicar.', AlertType.success);
      },
      error: error => {
        this._isLoadingButtonSalvar = false;
        this.buttonDesativado = false;
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  public gerarPrimeiroToken(): void {
    this._isLoadingButtonSalvar = true;
    this.buttonDesativado = true;

    this.acessoResulthLocalConnectService.generateToken(this.userLoginResponse.idEmpresa).subscribe({
      next: (tokenAcessoResulthLocalConnectResponse: TokenAcessoResulthLocalConnectResponse) => {
        this._isLoadingButtonSalvar = false;
        this.buttonDesativado = false;

        if (tokenAcessoResulthLocalConnectResponse.isError) {
          this.alertService.show(
            'Erro',
            tokenAcessoResulthLocalConnectResponse.errorDescription,
            AlertType.error
          );
          return;
        }

        const token = tokenAcessoResulthLocalConnectResponse?.token?.trim() ?? '';
        if (token === '') {
          this.alertService.show(
            'Erro',
            'Foi gerado um token vazio, por favor, tente novamente.',
            AlertType.error
          );
          return;
        }

        this.model.get('tokenResulthLocalConnect')?.setValue(token);
        this.model.get('tokenResulthLocalConnect')?.markAsDirty();
        this.model.get('tokenResulthLocalConnect')?.markAsTouched();

        this.configResulthLocalConnectChange();
      },
      error: error => {
        this._isLoadingButtonSalvar = false;
        this.buttonDesativado = false;
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  public copyToken(): void {
    const token = this.model.get('tokenResulthLocalConnect')?.value ?? '';
    if (!token?.trim()) {
      this.alertService.show('Aviso', 'Nenhum token para copiar.', AlertType.warning);
      return;
    }
    const ok = this.clipboard.copy(token);
    if (ok) {
      this.alertService.show('Copiado', 'Token copiado para a área de transferência.', AlertType.success);
    } else {
      this.alertService.show('Erro', 'Não foi possível copiar o token.', AlertType.error);
    }
  }

  somenteNumeros(event: any): void {
    let inputValue = event.target.value;
    inputValue = inputValue.replace(/\D/g, '');
    event.target.value = inputValue;
  }

  formatandoCampo(): void {
    const campoFormatado =
      this.model.get('codFilialBDResulthLocal')?.value.length == 1
        ? '0' + this.model.get('codFilialBDResulthLocal')?.value
        : this.model.get('codFilialBDResulthLocal')?.value;

    this.model.get('codFilialBDResulthLocal')?.setValue(campoFormatado);
  }

  configResulthLocalConnectChange(): void {
    this.habilitaTesteConexao =
      !this.model.get('tokenResulthLocalConnect')?.invalid &&
      !this.model.get('caminhoBDResulthLocal')?.invalid &&
      !this.model.get('hostBDResulthLocal')?.invalid &&
      !this.model.get('portaBDResulthLocal')?.invalid &&
      !this.model.get('usuarioBDResulthLocal')?.invalid &&
      !this.model.get('senhaBDResulthLocal')?.invalid &&
      !this.model.get('codFilialBDResulthLocal')?.invalid;
  }

  private async carregarTokenDaMatriz(): Promise<void> {
    if (this.isEmpresaMatriz) return;

    const idEmpresaMatriz = this._resulthLocalConnectResponse?.idEmpresaMatriz;

    if (!idEmpresaMatriz) {
      this.alertService.show(
        'Erro',
        'Não foi possível identificar a empresa matriz para carregar a token.',
        AlertType.error
      );
      this.model.get('usaTokenMatrizResulthLocalConnect')?.setValue(false, { emitEvent: false });
      return;
    }

    this._isLoadingButtonSalvar = true;
    this.buttonDesativado = true;

    try {
      const resp = await firstValueFrom(this.acessoResulthLocalConnectService.get(idEmpresaMatriz));

      this._isLoadingButtonSalvar = false;
      this.buttonDesativado = false;

      if (resp.isError) {
        this.alertService.show('Erro', resp.errorDescription, AlertType.error);
        this.model.get('usaTokenMatrizResulthLocalConnect')?.setValue(false, { emitEvent: false });
        return;
      }

      const tokenMatriz = resp.acessoResulthLocalConnect?.token?.trim() ?? '';
      if (!tokenMatriz) {
        this.alertService.show('Erro', 'A empresa matriz não possui uma token configurada.', AlertType.error);
        this.model.get('usaTokenMatrizResulthLocalConnect')?.setValue(false, { emitEvent: false });
        return;
      }

      this.model.get('tokenResulthLocalConnect')?.setValue(tokenMatriz);
      this.model.get('tokenResulthLocalConnect')?.disable({ emitEvent: false });
    } catch (error) {
      this._isLoadingButtonSalvar = false;
      this.buttonDesativado = false;
      this.alertService.show('Erro inesperado', error as any, AlertType.error);
      this.model.get('usaTokenMatrizResulthLocalConnect')?.setValue(false, { emitEvent: false });
    }
  }

  validaCamposTestarConexao(databaseRequest: DatabaseConnectionTestRequest): boolean {
    const camposInvalidos = [
      {
        condicao: !databaseRequest.tokenResulthLocalConnect,
        mensagem: 'O token informado é inválido',
      },
      { condicao: !databaseRequest.hostBD, mensagem: 'O host do banco informado é inválido' },
      { condicao: !databaseRequest.portaBD, mensagem: 'A porta do banco informado é inválido' },
      { condicao: !databaseRequest.caminhoBD, mensagem: 'O caminho do banco informado é inválido' },
      { condicao: !databaseRequest.usuarioBD, mensagem: 'O usuário do banco informado é inválido' },
    ];

    for (const campo of camposInvalidos) {
      if (campo.condicao) {
        this.alertService.show('Erro', campo.mensagem, AlertType.error);
        return false;
      }
    }

    return true;
  }

  async testarConexao(): Promise<void> {
    if (this._isLoadingButtonTestarConexao) return;

    const databaseRequest: DatabaseConnectionTestRequest = new DatabaseConnectionTestRequest();

    databaseRequest.tokenResulthLocalConnect = this.model.get('tokenResulthLocalConnect')?.value;
    databaseRequest.hostBD = this.model.get('hostBDResulthLocal')?.value;
    databaseRequest.portaBD = this.model.get('portaBDResulthLocal')?.value;
    databaseRequest.caminhoBD = this.model.get('caminhoBDResulthLocal')?.value;
    databaseRequest.usuarioBD = this.model.get('usuarioBDResulthLocal')?.value;

    if (
      this.model.get('senhaBDResulthLocal')?.value != '' &&
      this.model.get('senhaBDResulthLocal')?.value != this.placeholderSenhaBDResulthLocal
    ) {
      databaseRequest.senhaBD = this.model.get('senhaBDResulthLocal')?.value;
    } else {
      databaseRequest.senhaBD = this.bkpSenhaBDResulthLocal;
    }

    if (!this.validaCamposTestarConexao(databaseRequest)) return;

    this._isLoadingButtonTestarConexao = true;
    this.buttonDesativado = true;

    try {
      const returnStruct = await firstValueFrom(this.databaseService.post(databaseRequest));
      if (returnStruct.isError) {
        this.alertService.show('Erro', returnStruct.errorDescription, AlertType.error);
        return;
      }
      this.alertService.show('Sucesso', 'Conexão ao banco realizada com sucesso.', AlertType.success);
    } catch (error) {
      this.alertService.show('Erro inesperado', error as any, AlertType.error);
    } finally {
      this.buttonDesativado = false;
      this._isLoadingButtonTestarConexao = false;
    }
  }

  async submit(): Promise<void> {
    if (this._isLoadingButtonSalvar || this.model.invalid) {
      this.alertService.show('Erro', 'Verifique os campos em vermelho', AlertType.error);
      return;
    }

    const empresaResulthLocalConnectRequest: EmpresaResulthLocalConnectRequest =
      new EmpresaResulthLocalConnectRequest();
    empresaResulthLocalConnectRequest.utilizaResulthLocalConnect =
      this.model.get('utilizaResulthLocalConnect')?.value;

    const acessoRequest: AcessoResulthLocalConnectRequest = new AcessoResulthLocalConnectRequest();

    if (empresaResulthLocalConnectRequest.utilizaResulthLocalConnect) {
      acessoRequest.acessoResulthLocalConnect = new AcessoResulthLocalConnectStruct();
      acessoRequest.acessoResulthLocalConnect.token = this.model
        .get('tokenResulthLocalConnect')
        ?.value.trim();
      acessoRequest.acessoResulthLocalConnect.utilizaTokenMatriz =
        this.model.get('usaTokenMatrizResulthLocalConnect')?.value ?? false;
      acessoRequest.matriz = this.isEmpresaMatriz;
      acessoRequest.acessoResulthLocalConnect.hostBDResulthLocal = this.model
        .get('hostBDResulthLocal')
        ?.value.trim();
      acessoRequest.acessoResulthLocalConnect.portaBDResulthLocal =
        this.model.get('portaBDResulthLocal')?.value;
      acessoRequest.acessoResulthLocalConnect.caminhoBDResulthLocal = this.model
        .get('caminhoBDResulthLocal')
        ?.value.trim();
      acessoRequest.acessoResulthLocalConnect.usuarioBDResulthLocal = this.model
        .get('usuarioBDResulthLocal')
        ?.value.trim();
      acessoRequest.acessoResulthLocalConnect.codFilialBDResulthLocal = this.model
        .get('codFilialBDResulthLocal')
        ?.value.trim();

      if (
        this.model.get('senhaBDResulthLocal')?.value != '' &&
        this.model.get('senhaBDResulthLocal')?.value != this.placeholderSenhaBDResulthLocal
      ) {
        acessoRequest.acessoResulthLocalConnect.senhaBDResulthLocal =
          this.model.get('senhaBDResulthLocal')?.value;
      } else {
        acessoRequest.acessoResulthLocalConnect.senhaBDResulthLocal = this.bkpSenhaBDResulthLocal;
      }

      if (!acessoRequest.acessoResulthLocalConnect.senhaBDResulthLocal) {
        this.alertService.show('Erro', 'A senha do BD não está preenchida corretamente', AlertType.error);
        return;
      }

      if (
        this._acessoResulthLocalConnectResponse != null &&
        this._acessoResulthLocalConnectResponse.acessoResulthLocalConnect != null &&
        this._acessoResulthLocalConnectResponse.acessoResulthLocalConnect.idAcessoResulthLocalConnect != null &&
        this._acessoResulthLocalConnectResponse.acessoResulthLocalConnect.idAcessoResulthLocalConnect > 0
      ) {
        await this.alterarAcessoResulthLocalConnect(acessoRequest);
      } else {
        await this.inserirAcessoResulthLocalConnect(acessoRequest);
      }
    } else {
      await this.excluirAcessoResulthLocalConnect();
    }

    this.alterarUtilizaResulthLocalConnect(empresaResulthLocalConnectRequest);
  }

  private async inserirAcessoResulthLocalConnect(
    acessoRequest: AcessoResulthLocalConnectRequest
  ): Promise<boolean> {
    this._isLoadingButtonSalvar = true;
    this.buttonDesativado = true;

    try {
      const ret = await firstValueFrom(
        this.acessoResulthLocalConnectService.incluir(this.userLoginResponse.idEmpresa, acessoRequest)
      );

      this._isLoadingButtonSalvar = false;
      this.buttonDesativado = false;

      if (ret.isError) {
        this.alertService.show('Erro', ret.errorDescription, AlertType.error);
        return false;
      }
      this._acessoResulthLocalConnectResponse = ret;
      return true;
    } catch (error) {
      this._isLoadingButtonSalvar = false;
      this.buttonDesativado = false;
      this.alertService.show('Erro inesperado', error as any, AlertType.error);
      return false;
    }
  }

  private async alterarAcessoResulthLocalConnect(
    acessoRequest: AcessoResulthLocalConnectRequest
  ): Promise<boolean> {
    this._isLoadingButtonSalvar = true;
    this.buttonDesativado = true;

    try {
      const ret = await firstValueFrom(
        this.acessoResulthLocalConnectService.alterar(this.userLoginResponse.idEmpresa, acessoRequest)
      );

      this._isLoadingButtonSalvar = false;
      this.buttonDesativado = false;

      if (ret.isError) {
        this.alertService.show('Erro', ret.errorDescription, AlertType.error);
        return false;
      }
      return true;
    } catch (error) {
      this._isLoadingButtonSalvar = false;
      this.buttonDesativado = false;
      this.alertService.show('Erro inesperado', error as any, AlertType.error);
      return false;
    }
  }

  private async excluirAcessoResulthLocalConnect(): Promise<boolean> {
    this._isLoadingButtonSalvar = true;
    this.buttonDesativado = true;

    try {
      const ret = await firstValueFrom(
        this.acessoResulthLocalConnectService.excluir(this.userLoginResponse.idEmpresa, this.isEmpresaMatriz)
      );

      this._isLoadingButtonSalvar = false;
      this.buttonDesativado = false;

      if (ret.isError) {
        this.alertService.show('Erro', ret.errorDescription, AlertType.error);
        return false;
      }

      this._acessoResulthLocalConnectResponse = null;
      return true;
    } catch (error) {
      this._isLoadingButtonSalvar = false;
      this.buttonDesativado = false;
      this.alertService.show('Erro inesperado', error as any, AlertType.error);
      return false;
    }
  }

  private alterarUtilizaResulthLocalConnect(
    empresaResulthLocalConnectRequest: EmpresaResulthLocalConnectRequest
  ): void {
    this._isLoadingButtonSalvar = true;
    this.buttonDesativado = true;

    this.empresaService
      .alterarResulthLocalConnect(this.userLoginResponse.idEmpresa, empresaResulthLocalConnectRequest)
      .subscribe({
        next: (returnStruct: ReturnStruct) => {
          this._isLoadingButtonSalvar = false;
          this.buttonDesativado = false;
          if (returnStruct.isError) {
            this.alertService.show('Erro', returnStruct.errorDescription, AlertType.error);
            return;
          }
          this.alertService.show('Sucesso', 'Configuração salva com sucesso.', AlertType.success);
        },
        error: error => {
          this._isLoadingButtonSalvar = false;
          this.alertService.show('Erro inesperado', error, AlertType.error);
        },
      });
  }

  voltar(): void {
    this.router.navigate(['/integracao']);
  }

  abrirModalSaibaMais(): void {
    this.dialog.open(ResulthLocalConnectSaibaMaisDialogComponent, {
      width: '900px',
      maxHeight: '90vh',
    });
  }
}
